import classNames from "classnames";
import moment from "moment-timezone";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faClockRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { InputGroup, Tooltip } from "reactstrap";
import CustomTimeInput from "../../../../../components/Inputs/CustomTimeInput";
import { applyTimezone, combineDateAndTime } from "../../../../../utils/helpers/date";
import { useCompanyTimeFormat } from "../../../../../utils/hooks/company";
import { buildMissPunchPairUpdate, findContinuousMissPunchSibling, getTimeSheetTimeZone, getUnresolvedMissPunchSide, isAddedTimeSheet, isTimeInputDisabled } from "../helpers";
import { useAddedPayCode } from "../hooks/useAddedPayCode";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import { renderClockExceptionType } from "../../../Home/Actions/HistoryDrawer/ClockExceptions/useColumns";
import { MissPunchTooltip } from "./MissPunchTooltip";
import { getMandatoryBreak, isMandatoryBreakViolated } from "../../MandatoryBreakWarning/helpers";

export const getClockInputStyle = ({
    color,
    border,
    backgroundColor,
    value,
    original,
}) => {
    let style;
    if (value) {
        if (original.payCode?.hourType === "unpaid") {
            style = {
                color: "#00080e",
                backgroundColor: "#f2f1f3a6",
                border: `1px solid transparent`,
                padding: '0.2rem!important',
                fontSize: '0.9rem',
            };
        } else {
            style = {
                color,
                backgroundColor,
                border,
                padding: '0.2rem!important',
                fontSize: '0.9rem',
            };
        }
    } else {
        style = {
            color,
            backgroundColor: "transparent",
            border: `1px solid ${color}`,
            padding: '0.2rem!important',
            fontSize: '0.9rem',
        };
    }
    return style;
}

export const ExceptionTooltip = ({ original, view, exceptions, style }) => {
    const { t } = useTranslation();
    const { visible, toggle } = useVisible();
    const id = `exceptions${exceptions?.[0]?.id}`;

    return (
        <>
            <span id={id} className="px-1">
                <FontAwesomeIcon
                    className="text-sm cursor-pointer"
                    icon={faClockRotateLeft}
                    style={style}
                    onClick={() => view({ ...original, menu: "clock-exception" })}
                />
            </span>

            <Tooltip
                target={id}
                isOpen={visible}
                toggle={toggle}
            >
                <div className="d-flex flex-column">
                    {exceptions.map((exception) => (
                        <span className="mb-1">
                            {t(renderClockExceptionType(exception.type))}
                        </span>
                    ))}
                </div>
            </Tooltip>
        </>
    )
}

export const canTimeSheetBeBinded = (timeSheet) => {
    return timeSheet.payCode && !['amount', 'unit'].includes(timeSheet.payCode.hourType);
}

export const useInColumn = ({
    user,
    canEdit,
    clockExceptions,
    view,
    openInOutDateModal,
    openSyncTimesheetModal,
    openMissPunchModal,
    openMandatoryBreakWarning,
    isMyTimesheet,
}) => {
    const { t } = useTranslation();
    const inputRef = useRef();
    const timeFormat = useCompanyTimeFormat();
    const payCode = useAddedPayCode(user);

    const style = useMemo(() => {
        if (timeFormat) {
            switch (timeFormat.length) {
                case 1:
                case 2:
                case 3:
                case 4:
                case 5:
                    return { width: '60px' };
                case 6:
                case 7:
                    return { width: '70px' };
                case 8:
                case 9:
                case 10:
                    return { width: '80px' };
                default:
                    return { width: '116px' }
            }
        }
        return { width: '116px' }
    }, [timeFormat])

    return useMemo(() => ({
        Header: t("in"),
        accessor: "startDate",
        alwaysVisible: true,
        headCustomProps: { style },
        Cell: ({ value, row, cell, updateAddedTimeSheet, updateTimeSheet, data }) => {
            const { original, isNonProductive } = row;
            const disabled = isTimeInputDisabled(original) || row.disabled || cell.disabled || isNonProductive;
            const timeZone = getTimeSheetTimeZone(original);
            // A miss-punch whose start is the unknown side carries its end time in startDate as a
            // placeholder, so that the row has no invented duration. Showing it would read as a real
            // clock-in, so the cell is left empty for whoever fills it in.
            const missPunchSide = getUnresolvedMissPunchSide(original);
            const isMissingIn = missPunchSide === "start";
            const shownValue = isMissingIn ? undefined : value;
            const currentValue = shownValue
                ? timeZone
                    ? moment.parseZone(shownValue).toISOString(true)
                    : shownValue
                : undefined;
            const style = getClockInputStyle({
                color: isMissingIn ? "#ff6161" : "#00998b",
                backgroundColor: isMissingIn ? "#ff61611a" : "#00998b1a",
                border: `1px solid transparent`,
                value: shownValue,
                original,
            });

            if (isNonProductive || ["unit", "amount"].includes(original?.payCode?.hourType)) {
                return "-";
            }

            let exceptions = clockExceptions?.filter((exception) => moment(original?.date, "YYYY-MM-DD").isSame(moment(exception?.date, "YYYY-MM-DD"), "date"));
            exceptions = exceptions?.map((exception) => exception?.data);
            exceptions = (exceptions || []).flat();
            exceptions = exceptions?.filter((exception) =>
            (
                [
                    "came_early",
                    "came_late"
                ].includes(exception?.type) &&
                exception?.timesheet?.id === original?.id
            )
            );
            if (openInOutDateModal && original?.startDate && !isMissingIn) {
                style.borderLeft = 0;
            }

            return (
                <div className="d-flex align-items-center">
                    <InputGroup className="custom-time-input-group">
                        {(openInOutDateModal && original?.startDate && !isMissingIn && canEdit) && (
                            <span
                                className={classNames("clock-icon text-center", value && "filled",
                                    original?.payCode?.hourType === "unpaid" && "font-weight-400")}
                                style={{
                                    ...style,
                                    padding: "0.1rem 0.4rem",
                                }}
                            >
                                <FontAwesomeIcon
                                    className="cursor-pointer"
                                    icon={faClock}
                                    onClick={() => openInOutDateModal({
                                        column: "in",
                                        user,
                                        original,
                                        updateAddedTimeSheet,
                                        updateTimeSheet
                                    })}
                                    style={{ fontSize: "1rem", paddingTop: "0.2rem" }}
                                />
                            </span>
                        )}
                        <CustomTimeInput
                            ref={inputRef}
                            timeFormat={timeFormat}
                            className={classNames("time-input text-center", shownValue && "filled", (shownValue && canEdit) && "input-with-value",
                                isMissingIn && "miss-punch-input",
                                original?.payCode?.hourType === "unpaid" && "font-weight-400")}
                            style={style}
                            disabled={disabled || !canEdit}
                            placeholder={t("time")}
                            value={currentValue}
                            onChange={(val) => {
                                let startDate = "";
                                const updatedFields = {}
                                if (val) {
                                    startDate = combineDateAndTime(
                                        moment(original.date, "YYYY-MM-DD"),
                                        moment(val),
                                    );
                                    startDate = applyTimezone(startDate, timeZone, true);

                                    if (original.endDate) {
                                        if (startDate.date() !== moment.parseZone(original.endDate).date()) {
                                            let endDate = combineDateAndTime(
                                                moment(original.date, "YYYY-MM-DD"),
                                                moment.parseZone(original.endDate),
                                            );
                                            endDate = applyTimezone(endDate, timeZone, true);
                                            if (endDate.isAfter(startDate, "minute")) {
                                                updatedFields.endDate = endDate.toISOString(true);
                                            }
                                        }
                                        if (moment(original.endDate).isBefore(startDate)) {
                                            updatedFields.endDate = moment
                                                .parseZone(original.endDate)
                                                .add(1, "day")
                                                .toISOString(true);
                                        }
                                    }
                                    startDate = startDate.toISOString(true);
                                }

                                // update sibling
                                // ?? maybe on binded field change, open a modal to confirm the binding change
                                // ?? should bind continue if the change causes a cut / conflict / other issues
                                // TODO: bind should stop if user unbinded the timesheets
                                if (original.startDate && canTimeSheetBeBinded(original) && openSyncTimesheetModal) {
                                    const sibling = data.find((item) => {
                                        if (!item.endDate || !canTimeSheetBeBinded(item)) {
                                            return false;
                                        }
                                        if (item.id === original.id) {
                                            return false;
                                        }
                                        return moment.parseZone(item.endDate)
                                            .isSame(moment.parseZone(original.startDate), "minute");
                                    });
                                    // TODO: detect swallous / conflicting timesheets / other issues due to change / binding
                                    if (sibling) {
                                        const endDate = startDate;
                                        const onSuccess = () => {
                                            const callback = isAddedTimeSheet(sibling)
                                                ? updateAddedTimeSheet.bind(null, sibling)
                                                : updateTimeSheet.bind(null, sibling.id)
                                            callback({ endDate });
                                        }
                                        openSyncTimesheetModal({
                                            field: t("end-date").toLowerCase(),
                                            onSuccess,
                                        })
                                    }
                                }

                                const duration = original?.endDate && startDate
                                    ? moment(original.endDate).diff(moment(startDate), "seconds")
                                    : original?.duration || 0;

                                updatedFields.startDate = startDate;
                                updatedFields.duration = duration;

                                const addedTimesheet = isAddedTimeSheet(original);
                                if (addedTimesheet && !original?.payCode) {
                                    updatedFields.payCode = payCode;
                                }
                                const changeValue = (data) => {
                                    if (!val) {
                                        return;
                                    }
                                    // One voided punch leaves two rows sharing a single unknown moment:
                                    // the segment before it has no end, this one has no start. Supplying
                                    // either answers both.
                                    const missPunchPair = startDate
                                        ? findContinuousMissPunchSibling(original, data)
                                        : undefined;
                                    const pairFields = buildMissPunchPairUpdate(missPunchPair, startDate);
                                    if (pairFields) {
                                        const pairCallback = isAddedTimeSheet(missPunchPair)
                                            ? updateAddedTimeSheet.bind(null, missPunchPair)
                                            : updateTimeSheet.bind(null, missPunchPair.id);
                                        pairCallback(pairFields);
                                    }
                                    if (isAddedTimeSheet(original)) {
                                        updateAddedTimeSheet(original, data);
                                    } else {
                                        updateTimeSheet(original.id, data);
                                    }
                                }
                                const mandatoryBreak = openMandatoryBreakWarning ? getMandatoryBreak(original) : null;
                                if (mandatoryBreak) {
                                    const inViolation = isMandatoryBreakViolated(
                                        {
                                            ...original,
                                            ...updatedFields,
                                            payCode: original.payCode || updatedFields.payCode,
                                        },
                                        mandatoryBreak,
                                    );
                                    if (inViolation) {
                                        openMandatoryBreakWarning({
                                            column: "in",
                                            requiredTime: mandatoryBreak.duration,
                                            original,
                                            updatedFields,
                                            onConfirm: () => {
                                                changeValue(updatedFields);
                                            },
                                            onCancel: () => {
                                                // WORKAROUND FOR TIME INPUT
                                                inputRef.current?.reset();
                                            },
                                        });
                                    } else {
                                        changeValue(updatedFields);
                                    }
                                } else {
                                    changeValue(updatedFields);
                                }
                            }}
                        />
                    </InputGroup>

                    {(exceptions?.length > 0 && view) ? (
                        <ExceptionTooltip
                            original={original}
                            view={view}
                            exceptions={exceptions}
                            style={{
                                color: "#00998b",

                            }}
                        />
                    ) : null}

                    {(isMissingIn && openMissPunchModal) && (
                        <MissPunchTooltip
                            openMissPunchModal={openMissPunchModal}
                            original={original}
                            isMyTimesheet={isMyTimesheet}
                            side="start"
                        />
                    )}
                </div>
            );
        },
    }), [
        t,
        style,
        clockExceptions,
        openInOutDateModal,
        timeFormat,
        canEdit,
        view,
        user,
        payCode,
        openSyncTimesheetModal,
        openMissPunchModal,
        openMandatoryBreakWarning,
        isMyTimesheet,
    ]);
};
